import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, Output, EventEmitter, ElementRef, AfterViewInit, ViewChild, NgZone, Inject, OnDestroy, ChangeDetectorRef, HostListener, Renderer2, OnInit, AfterContentInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subscription, Observable, interval, timer } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { AppState } from '../../interfaces/app-state.interface';
import { MediaAction } from '../../state-management/media/media.action';
import { TrPlayerAction } from './enums/tr-player-action.enum';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { ClipInterceptor } from './models/clip-interceptor.model';
import { TrPlayerSession } from './models/tr-player-session.model';
import { UtilitiesService } from '../../services/utilities.service';
import { TrPlaylistComponent } from './components/tr-playlist/tr-playlist.component';
import { TrPlayerMediaService } from './services/tr-player-media.service';
import { TrVideoResumeComponent } from './components/tr-video-resume/tr-video-resume.component';
import { TrShareCourseComponent } from './components/tr-share-course/tr-share-course.component';
import { TrPlayerSoundTrackService } from './services/tr-player-soundtrack.service';
import { TrPlayerSessionManagerService } from './services/tr-player-session-manager.service';
import * as scriptjs from 'scriptjs';

export const PLAYER_SETTINGS = {
    lib: 'https://cdn1.raptmedia.com/system/scripts/api.v2.min.js.gz',
    pauseKey: 32,
    language: 'en',
    soundTrack: 'assets/audio/1.mp3',
    minWatchedPercent: 80,
};

@AutoUnsubscribe()
@Component({
    selector: 'tr-player',
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None,
    templateUrl: './tr-player.component.html',
    styleUrls: ['./tr-player.component.scss'],
})
export class TrPlayerComponent implements OnInit, OnDestroy {

    @Input() public shareLink: string;
    @Input() public projectId: string;
    @Input() public soundTrack: string = PLAYER_SETTINGS.soundTrack;
    @Input() public interceptors: ClipInterceptor[] = [];
    @Input() public mediaGroupId: string;
    @Input() public inactivityTime: number = 4000;
    @Input() public enablePlaylist: boolean = true;
    @Input() public allowFullscreen: boolean;
    @Input() public initialVideoTitle: string;
    @Input() public soundTrackLoopStart: number = 0;
    @Input() public continuePreviousSession: boolean;
    @Input() public disableSoundTrackOnFirstVideo: boolean = false;

    @Output() public onProjectEnd: EventEmitter<any> = new EventEmitter<any>();
    @Output() public onProjectStart: EventEmitter<any> = new EventEmitter<any>();
    @Output() public onCloseRequest: EventEmitter<Event> = new EventEmitter<Event>();
    @Output() public onProjectPaused: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('resume', {static: true}) public resume: TrVideoResumeComponent;
    @ViewChild('playlist', {static: true}) public playlist: TrPlaylistComponent;
    @ViewChild('shareOverlay', {static: true}) public shareOverlay: TrShareCourseComponent;
    @ViewChild('playerBackdrop', {static: true}) public playerBackdrop: ElementRef;
    @ViewChild('projectContainer', {static: true}) public projectContainer: ElementRef;

    public ready: boolean;
    public muted: boolean;
    public playing: boolean;
    public playerId: string;
    public duration: string;
    public currentTime: string;
    public currentNode: any;
    public lockControls: boolean;
    public backdropVisible: boolean;
    public captionsEnabled: boolean;
    public currentVideoSlug: string;
    public hasPreviousVideo: boolean;
    public fullscreenEnabled: boolean;
    public progressPercentage: number = 0;
    public showBottomControls: boolean;
    public maxUserTimingPercent: number;
    public orientationChangeListenner: (...params) => any;
    private nodes: any[] = [];
    private raptor: any;
    private window: Window;
    private session: TrPlayerSession;
    private autoplay: boolean = false;
    private firstClick: boolean = true;
    private mediaUpdating: boolean;
    private projectLoaded: boolean;
    private windowHasFocus: boolean;
    private keepSoundTrack: boolean;
    private navigationHistory: string[] = [];
    private userAuthenticated: boolean;
    private mediaSubscription: Subscription;
    private mediaWatchingTracked: boolean;
    private playWhenRecoverFocus: boolean;
    // tslint:disable-next-line:ban-types
    private rendererSubscription: Function;
    private lockControlsVisiblityByMouseOver: boolean;
    private showBottomControlsTimerSubscription: Subscription;
    private authenticationSubscription: Subscription;
    private trackingPlayerActivySubscription: Subscription;

    constructor(
        private zone: NgZone,
        private store: Store<AppState>,
        private renderer: Renderer2,
        private route: ActivatedRoute,
        private router: Router,
        private detectChange: ChangeDetectorRef,
        private changeDetector: ChangeDetectorRef,
        private sessionManager: TrPlayerSessionManagerService,
        private elementReference: ElementRef,
        private utilitiesService: UtilitiesService,
        private trPlayerMediaService: TrPlayerMediaService,
        private trPlayerSoundTrackService: TrPlayerSoundTrackService,
        @Inject(DOCUMENT) private document: any,
    ) {
        this.window = window;
        this.playerId = this.trPlayerMediaService.generatePlayerGuid();
        this.session = this.sessionManager.getPreviousSessionInformation();

        this.authenticationSubscription = this.store
            .select((state) => state.authentication.currentUser)
            .subscribe((user) => this.userAuthenticated = user && user.token);

        this.mediaSubscription = this.store
            .select((state) => state.media.loading)
            .subscribe((pending) => this.mediaUpdating = pending);

        this.trPlayerSoundTrackService.setLoopStart(this.soundTrackLoopStart);

        this.route.queryParams.subscribe((params: Params) => {
            this.autoplay = params.autoplay === 'true';
        });
    }

    /**
     * handler para keypress
     * @param event evento
     */
    @HostListener('document:blur', ['$event'])
    public hanlderKeyPress(event: KeyboardEvent) {
        this.pause(true, true, true, true);
    }

    public ngOnDestroy() {
        this.changeDetector.detach();
        this.trPlayerSoundTrackService.stop();
        this.trPlayerSoundTrackService.reset();
        this.raptor.api.off('play', null);
        this.raptor.api.off('pause', null);
        this.raptor.api.off('clipStart', null);
        this.raptor.api.off('clipEnd', null);
        this.raptor.api.off('projectStart', null);
        this.raptor.api.off('projectEnd', null);
        this.raptor.api.off('ready', null);
        this.raptor.api.off('timeUpdate', null);
        this.raptor.api.off('inboundReady', null);
        this.raptor.api.off('error', null);
        this.raptor.api.off('button', null);

        if (this.fullscreenEnabled) this.toggleFullscreen();
        if (this.rendererSubscription) this.rendererSubscription();
    }

    public get showCloseButton(): boolean {
        return this.showBottomControls
            && !this.playlist.visible
            || (
                !this.playing
                && this.progressPercentage > 0
                && this.progressPercentage < 100
                && this.resume.showPlayButton
                && !this.playlist.visible
            );
    }

    public ngOnInit() {
        scriptjs([PLAYER_SETTINGS.lib], 'raptorLib', () => {
            this.raptor = (window as any).raptor;
            this.utilitiesService.isMobile() ? this.loadProjectMobile() : this.loadProject();
        });
    }

    /**
     * Carrega o projeto interativo versão mobile
     */
    public loadProjectMobile() {
        if (this.window.innerWidth > this.window.innerHeight) {
            this.loadProject();
        } else {
            this.rendererSubscription = this.renderer.listen('window', 'resize', (event) => {
                if (this.window.innerWidth > this.window.innerHeight && !this.projectLoaded) {
                    this.loadProject();
                }
            });
        }
    }

    public bottomControlsMouseOverHandler(event: Event) {
        this.lockControlsVisiblityByMouseOver = true;
        this.mouseMoveHandler();
    }

    public bottomControlsMouseOutHandler(event: Event) {
        this.lockControlsVisiblityByMouseOver = false;
    }

    /**
     * Carrega o projeto interativo
     */
    public loadProject() {

        if (this.projectLoaded) return;

        this.projectLoaded = true;
        this.raptor = (this.window as any).raptor;

        const projectIframe = this.trPlayerMediaService.createEmbedProjectElement(this.projectId);

        // projectIframe.onload = (event) => {
        //     // this.raptor.api.launch(projectIframe.name);
        //     // this.trackPlayerActivit();
        // };

        this.raptor.api.on('ready', (event, frame) => {

            this.raptor.settings('defaultIFrame', frame.name);
            frame.onload = () => {
                this.raptor.api.launch(frame.name);
                this.trackPlayerActivit();
            };

        });

        this.projectContainer.nativeElement.appendChild(projectIframe);
        this.raptor.api.initByIframe(projectIframe);

        // Carrega a trilha de fundo
        this.trPlayerSoundTrackService.load(this.soundTrack);

        // Assiste as atualizacoes de tempo
        this.raptor.api.on('timeUpdate', (event, data) => {
            if (data) {
                this.duration = this.trPlayerMediaService.formatTime(data.duration);
                this.currentTime = this.trPlayerMediaService.formatTime(data.time);
            }
        });

        // Api disponivel
        this.raptor.api.on('inboundReady', (event, data) => {

            if (this.initialVideoTitle) {

                const videoId = this.getVideoSourceId(this.initialVideoTitle);

                if (videoId > -1) {
                    this.raptor.api.setNode(videoId.toString());
                    this.currentNode = {title: this.getVideoNameBySourceId(videoId.toString()), duration: 0};
                    this.resume.show(true, false);
                }
            } else {

                this.resume.show(true, false);
            }

            // if (this.autoplay) {
                // this.setMute(true);
            this.raptor.api.play();
            // }

            this.ready = true;
            this.mouseMoveHandler();

        });

        // Início do projeto
        // this.raptor.api.on('projectStart', (event, data) => {
        //     //
        // });

        // Fim do projeto
        this.raptor.api.on('projectEnd', (event, data) => {
            this.trPlayerSoundTrackService.pause();
        });

        this.raptor.api.on('clipStart', (event, data) => {

            if (this.raptor.api.state().isStart) {

                if (!this.disableSoundTrackOnFirstVideo) this.trPlayerSoundTrackService.play();
                if (this.disableSoundTrackOnFirstVideo) this.trPlayerSoundTrackService.stop();

            } else {
                this.trPlayerSoundTrackService.play();
            }

            if (this.resume.visible) this.resume.hide();

            this.backdropVisible = true;
            this.currentNode = {title: data.clipname, duration: data.duration};
            this.currentVideoSlug = this.utilitiesService.createSlug(data.clipname);
            this.session.lastClip = this.raptor.api.state().id;
            this.sessionManager.setSessionInformation(this.session);
            this.computeMaxUserTimingPercent();
            this.mediaWatchingTracked = false;
        });

        this.raptor.api.on('clipEnd', (event, data) => {

            this.showBottomControls = false;
            if (this.resume.visible) this.resume.hide();
            this.trPlayerSoundTrackService.fadeIn();

        });

        // Clip iniciado
        this.raptor.api.on('play', (data) => {

            // Trilha sonora
            if (this.raptor.api.state().isStart) {
                if (!this.disableSoundTrackOnFirstVideo) this.trPlayerSoundTrackService.play();
                if (this.disableSoundTrackOnFirstVideo) this.trPlayerSoundTrackService.stop();
            } else {
                this.trPlayerSoundTrackService.play();
            }

            if (!this.raptor.api.state().isStart) this.trPlayerSoundTrackService.fadeOut();

            // Mostra backdrop e botão de paus
            this.backdropVisible = true;
            this.playing = true;

            // Estado atual
            const currentState = this.raptor.api.state();
            this.hasPreviousVideo = !currentState.isStart;

            // Track do histórico
            this.registerNavigation(currentState.id.toString());

            // Esconde tela de resumo
            if (this.resume) this.resume.hide();

            // Esconde controles da parte inferior
            this.showBottomControls = false;

        });

        // Assiste as atualizacoes de tempo
        // this.raptor.api.on('timeUpdate', (event, data) => {
        //     if(data.clipname == '', + data.time
        // });

        // Clip pausado
        this.raptor.api.on('pause', (event, data) => {

            // Trilha sonora
            this.backdropVisible = false;
            this.playing = false;

            // Trilha para o primeiro plano
            if (+data.progress !== 100 && !this.keepSoundTrack) this.trPlayerSoundTrackService.pause();
            else this.trPlayerSoundTrackService.play();

            this.keepSoundTrack = false;

            if (!this.resume.visible && this.progressPercentage < 95) this.resume.show(true, true);

        });

        // Erros
        // this.raptor.api.on('error', (data) => {
        //     //
        // });

        // Button events (with api data)
        this.raptor.api.on('button', (event, data) => {

            // Replay
            if (data.action && data.action.match( new RegExp(`^${TrPlayerAction.ReplayVideo.toString()}`, 'i'))) {
                const currentState = this.raptor.api.state();
                this.resetInterceptors(currentState.name);
                this.play();
            }

            // Action
            // if(data.action && data.action.match( new RegExp(`^${TrPlayerAction.FireAction.toString()}`, 'i')))

            // Navegar
            if (data.action && data.action.match( new RegExp(`^${TrPlayerAction.Navigate.toString()}`, 'i'))) {
                this.pause();
                const url = data.action.match(/\(([^)]+)\)/)[1];
                this.router.navigateByUrl(url);
            }

            // Esconde os botões quando realiza uma ação
            this.showBottomControls = false;
        });

        this.zone.runOutsideAngular(() => {
            this.playerBackdrop.nativeElement.addEventListener('mousemove', this.mouseMoveHandler.bind(this));
        });
    }

    /**
     * Handler para seek do usuário na barra de progresso
     * @param timePercentage percentual do tempo
     */
    public onClientSeek(timePercentage: number) {

        // Caso o usuário tenha voltado o vídeo os interceptors adiante precisam ser resetados
        if (this.progressPercentage > timePercentage) {

            const time = timePercentage * 100 / this.currentNode.duration;

            this.interceptors
            .filter((interceptor) => interceptor.time >= time)
            .filter((interceptor) => interceptor.called = false);

        }

        this.raptor.api.setTimePercentage(timePercentage / 100);
    }

    /**
     * Inicia/continua a reprodução de um vídeo. Caso o vídeo esteja finalizado ele irá repoduzir do início
     */
    public play() {

        // if(!this.raptor.api.state().progressPercentage)//sem efeito de fadeOut no primeiro play
        // this.trPlayerSoundTrackService.fadeOut();
        // else
        //  this.trPlayerSoundTrackService.play();

        if (this.playlist.visible) this.playlist.close();

        this.raptor.api.play();
        this.resume.hide();
        this.lockControls = false;
    }

    /**
     * Pausa do vídeo corrente
     * @param showPlayButton mostrar o botão de play
     * @param showContentDescription mostrar o título do vídeo
     */
    public pause(
        showPlayButton: boolean = true,
        showContentDescription: boolean = true,
        showCloseButton: boolean = true,
        lockControls: boolean = false,
        fromUser: boolean = true,
    ) {

        this.lockControls = lockControls;
        this.keepSoundTrack = !fromUser;
        this.raptor.api.pause();
        this.resume.show(showPlayButton, showContentDescription);
    }

    /**
     * Handler para fechar o player
     *
     * @param  {Event} event
     */
    public closeFromResume(event: Event) {
        this.onCloseRequest.emit(event);
    }

    /**
     *  Alterna entre os estados de mudo enão mudo
     */
    public toggleMuted() {

        this.setMute(!this.muted);
        this.sessionManager.setSessionInformation(this.session);

        // Pause e resume da trilha
        if (this.muted) {
            this.trPlayerSoundTrackService.pause();
        } else {
            if (this.raptor.api.state().isStart) {
                if (!this.disableSoundTrackOnFirstVideo) this.trPlayerSoundTrackService.play();
                if (this.disableSoundTrackOnFirstVideo) this.trPlayerSoundTrackService.stop();
            } else {
                this.trPlayerSoundTrackService.play();
            }
        }

    }

    /**
     * Reproduz o vídeo anterior
     * @param  {Event} event
     */
    public previousVideo(event: Event) {

        const videoId = this.navigationHistory[this.navigationHistory.length - 2];
        this.navigationHistory = this.navigationHistory.slice(0, this.navigationHistory.length - 2);
        this.raptor.api.setNode(videoId);
        this.raptor.api.play();

    }

    /** Mostra a playlist do projeto */
    public togglePlaylist() {

        if (this.playlist.visible) {
            this.playlist.close();
            this.play();
        } else {
            this.playlist.show();
            this.pause(false, false, true);
        }
    }

    /**
     * Mostra o overlay de compartilhamento
     */
    public share() {

        if (this.shareOverlay.visible) {
            this.shareOverlay.hide();
            if (!this.playing) return this.play();
        }

        if (this.playing) this.pause();

        this.shareOverlay.show();

        if (this.resume.visible) {

            this.resume.show(false, this.resume.showContentDescription);
            this.shareOverlay.onClose.subscribe((result) => {
                if (this.resume.visible) this.resume.show(true, this.resume.showContentDescription);
            });

        }
    }

    /**
     * Handler para seleção de vídeos da playlist
     *
     * @param video video selecionado
     */
    public onPlaylistSelectedVideo(video: any) {

        const sourceId = this.getVideoSourceId(video.title);

        if (sourceId > 0) this.raptor.api.setNode(sourceId);

        this.raptor.api.play();
    }

    public onPlaylistClose() {
        this.play();
    }

    /**
     * Toggle fullscreen mode
     */
    public toggleFullscreen() {

        if (this.fullscreenEnabled) this.utilitiesService.exitFullscreen(this.document);
        else this.utilitiesService.goFullscreen(this.document.documentElement as any);

        this.fullscreenEnabled = !this.fullscreenEnabled;
    }

    /**
     * Handler para clicks no backgrip
     *
     * @param  {Event} event
     */
    public onBackdropClick(event: Event) {

        if (this.firstClick) {
            if (this.autoplay) this.setMute(false);

            this.firstClick = false;
            this.mouseMoveHandler();

        } else {
            if (this.utilitiesService.isMobile()) this.firstClick = true;
            if (this.playing) this.pause();
        }
    }

    /**
     * Habilita/desabilitar a legenda
     */
    public toggleCaptions() {

        if (this.captionsEnabled) this.raptor.api.setTrack('hide');
        if (!this.captionsEnabled) this.raptor.api.setTrack(PLAYER_SETTINGS.language);

        this.captionsEnabled = !this.captionsEnabled;
        this.session.captionsEnabled = this.captionsEnabled;
        this.sessionManager.setSessionInformation(this.session);
    }

    /**
     * Registra um ato de navegação
     * @param videoId id do vídeo
     */
    private registerNavigation(videoId: string) {
        if (this.navigationHistory.length === 0) {
            this.navigationHistory.push(videoId);
        } else {
            if (this.navigationHistory[this.navigationHistory.length - 1] !== videoId) {
                this.navigationHistory.push(videoId);
            }
        }
    }

    /** Recupera o id do clip no raptmedia baseando-se no slug do video */
    private getVideoSourceId(title: string) {

        const nodes = this.raptor.api.nodes();
        let id = -1;
        for (const key in nodes) {
            if (this.utilitiesService.createSlug(key) === this.utilitiesService.createSlug(title)) id = +nodes[key];
        }

        return id;
    }

    private getVideoNameBySourceId(id: string) {

        const nodes = this.raptor.api.nodes();

        for (const key in nodes) {
            if (nodes[key] === id) return key;
        }

        return null;
    }

    /**
     * Monitora a atividade do player. Progresso do vídeo.
     */
    private trackPlayerActivit() {

        this.trackingPlayerActivySubscription = interval(500)
            .pipe(startWith(0))
            .subscribe((time) => {

                const currentState = this.raptor.api.state();
                if (!currentState) return false;

                this.progressPercentage = currentState.progressPercentage || 0;
                this.checkInterceptors(currentState.name, currentState.progressTime);

                // Realizar track do video
                if (
                    this.userAuthenticated
                    && !this.mediaUpdating
                    && !this.mediaWatchingTracked
                    && this.progressPercentage >= PLAYER_SETTINGS.minWatchedPercent
                ) {
                    this.mediaWatchingTracked = true;
                    const currentVideo = this.playlist.getCurrentVideo();

                    if (currentVideo) {
                        this.store.dispatch({
                            type: MediaAction.markVideoAsSeen,
                            payload: {
                                id: currentVideo.mediaID,
                                progress: PLAYER_SETTINGS.minWatchedPercent,
                            },
                        });
                    }
                }
        });
    }

    private setMute(isMute: boolean = false) {
        this.muted = isMute;
        this.session.muted = this.muted;
        this.trPlayerSoundTrackService.mute(this.muted);
        this.raptor.api.setMute(this.muted);
    }

    /**
     * Hanlder para quando o usuário movimento
     * o mouse sobre a tela do player
     * @param event evento de mouse
     */
    private mouseMoveHandler(event: Event = null) {

        if (this.lockControlsVisiblityByMouseOver) {
            // this.showBottomControls = true;
            if (this.showBottomControlsTimerSubscription) return this.showBottomControlsTimerSubscription.unsubscribe();
        }

        if (this.showBottomControlsTimerSubscription) this.showBottomControlsTimerSubscription.unsubscribe();

        this.showBottomControls = true;

        this.showBottomControlsTimerSubscription = timer(this.inactivityTime)
            .subscribe(() => {
                if (this.playing) {
                    this.showBottomControls = false;
                    this.firstClick = true;
                }
            });

    }

    /**
     * Verifica interceptors a serem chamados
     *
     * @param nodeName nome do nodo/clip corrente
     * @param time tempo em segundos decorridos no clip corrente
     */
    private checkInterceptors(nodeName: string, time: number) {

        if (!this.playing) return false;

        const elegibleInterceptors = this.interceptors.filter( (item: ClipInterceptor) => {
            return item.shouldBeCalled(nodeName, time);
        });

        elegibleInterceptors.map((item: ClipInterceptor) => {
            item.callback(item);
        });
    }

    /**
     * Reseta interceptors do clip
     *
     * @param nodeName nome do clip
     */
    private resetInterceptors(nodeName: string) {
        this.interceptors
            .filter((interceptor) => nodeName.match(new RegExp(interceptor.clipName, 'gi')))
            .forEach((interceptor) => interceptor.markAsCalled(false));
    }

    private togglePlay() {
        if (this.playing) this.pause();
        else this.play();
    }

    /**
     * Define o percentual máximo que o usuário pode avançar sem prejudicar a experiência com as interações
     */
    private computeMaxUserTimingPercent() {

        const relatedInterceptors = this.interceptors.filter(
            (interceptor) => interceptor.clipName.match(new RegExp(this.currentNode.title, 'gi')),
        );

        if (relatedInterceptors.length === 0) return this.maxUserTimingPercent = 101;

        const firstInterceptor = relatedInterceptors.sort((a, b) => a.time - b.time )[0];
        const duration = this.raptor.api.state().duration;

        this.maxUserTimingPercent = firstInterceptor.time * 100 / duration;

    }

}
